import { useCallback, useEffect, useState } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Lock, LogOut, RefreshCw } from "lucide-react";
import { Players } from "@/components/site/stats/Players";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN = "sb-stats-token";
const RANGES = [[1, "Today"], [7, "7 days"], [30, "30 days"], [90, "90 days"]];
const PAGE_NAMES = { "/": "Home", "/shop": "Shop", "/build": "Build your box", "/story": "Story", "/reviews": "Reviews",
  "/nutrition": "Nutrition", "/stores": "Stores", "/faq": "FAQ" };
const pageName = (p) => PAGE_NAMES[p] || (p.startsWith("/flavours/") ? `Flavour: ${p.split("/")[2]}` : p);
const errText = (d) => (typeof d === "string" ? d : "Something went wrong. Please try again.");

const Login = ({ onToken }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API}/stats/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password: pw }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(errText(d?.detail));
      onToken(d.token);
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="stats-login-form" className="mx-auto mt-24 w-full max-w-sm rounded-3xl bg-white p-8 shadow-[0_20px_60px_-30px_rgba(27,21,48,0.4)]">
      <Lock className="h-6 w-6 text-[#FF5A5F]" />
      <h1 className="font-display mt-4 text-3xl">Store stats</h1>
      <p className="mt-1 text-sm text-[#4A4560]">Private. Owner password only.</p>
      <input data-testid="stats-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" autoFocus
        className="mt-6 h-12 w-full rounded-full border border-[#1B1530]/15 px-5 outline-none focus:border-[#FF5A5F]" />
      {err && <p data-testid="stats-login-error" className="mt-3 text-sm font-semibold text-[#E8233F]">{err}</p>}
      <button data-testid="stats-login-button" disabled={busy || !pw} className="mt-4 h-12 w-full rounded-full bg-[#1B1530] font-bold text-white transition-colors hover:bg-[#FF5A5F] disabled:opacity-50">
        {busy ? "Checking..." : "Sign in"}
      </button>
    </form>
  );
};

const Card = ({ label, value, testid, accent }) => (
  <div data-testid={testid} className={`rounded-3xl p-5 ${accent ? "bg-[#1B1530] text-white" : "bg-white"}`}>
    <p className={`text-xs font-bold uppercase tracking-wider ${accent ? "text-white/70" : "text-[#4A4560]"}`}>{label}</p>
    <p className="font-display mt-2 text-4xl">{(value ?? 0).toLocaleString()}</p>
  </div>
);

const List = ({ title, rows, testid, label = (x) => x }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div data-testid={testid} className="rounded-3xl bg-white p-5">
      <h2 className="text-base font-bold">{title}</h2>
      {rows.length ? rows.map((r) => (
        <div key={r.name} className="mt-3">
          <div className="flex justify-between text-sm"><span className="truncate pr-3">{label(r.name)}</span><span className="font-bold">{r.count}</span></div>
          <div className="mt-1 h-1.5 rounded-full bg-[#1B1530]/5"><div className="h-1.5 rounded-full bg-[#FF5A5F]" style={{ width: `${(r.count / max) * 100}%` }} /></div>
        </div>
      )) : <p className="mt-3 text-sm text-[#4A4560]">No data yet.</p>}
    </div>
  );
};

const Chart = ({ daily }) => (
  <div data-testid="stats-daily-chart" className="rounded-3xl bg-white p-5">
    <h2 className="text-base font-bold">Visitors per day</h2>
    <div className="mt-4 h-56">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={daily} margin={{ left: -20, right: 8 }}>
          <XAxis dataKey="day" tickFormatter={(d) => d.slice(5)} fontSize={12} stroke="#4A4560" />
          <YAxis allowDecimals={false} fontSize={12} stroke="#4A4560" />
          <Tooltip />
          <Area type="monotone" dataKey="visitors" stroke="#FF5A5F" fill="#FF5A5F" fillOpacity={0.2} strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const Dashboard = ({ s }) => {
  const conv = s.visitors ? ((s.checkout_clicks / s.visitors) * 100).toFixed(1) : "0.0";
  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card testid="stats-live-now" label="On the site now" value={s.live_now} accent />
        <Card testid="stats-visitors" label="Visitors" value={s.visitors} />
        <Card testid="stats-page-views" label="Page views" value={s.page_views} />
        <Card testid="stats-all-time" label="All-time visitors" value={s.all_time_visitors} />
        <Card testid="stats-add-to-carts" label="Add to carts" value={s.add_to_carts} />
        <Card testid="stats-checkouts" label="Went to checkout" value={s.checkout_clicks} />
        <Card testid="stats-sessions" label="Visits (sessions)" value={s.sessions} />
        <div data-testid="stats-conversion" className="rounded-3xl bg-[#FFB21A] p-5">
          <p className="text-xs font-bold uppercase tracking-wider">Visitors to checkout</p>
          <p className="font-display mt-2 text-4xl">{conv}%</p>
        </div>
      </div>
      {s.days > 1 && <div className="mt-3"><Chart daily={Array.isArray(s.daily) ? s.daily : []} /></div>}
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <List testid="stats-top-pages" title="Top pages" rows={s.top_pages || []} label={pageName} />
        <List testid="stats-sources" title="Where visitors came from" rows={s.sources || []} />
        <List testid="stats-devices" title="Devices" rows={[{ name: "Phone", count: s.devices?.mobile ?? 0 }, { name: "Desktop / laptop", count: s.devices?.desktop ?? 0 }]} />
      </div>
    </>
  );
};

export default function StatsPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN));
  const [days, setDays] = useState(7);
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);

  const signIn = (t) => { localStorage.setItem(TOKEN, t); localStorage.setItem("sb-no-track", "1"); setToken(t); };
  const signOut = () => { localStorage.removeItem(TOKEN); setToken(null); setS(null); };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/stats?days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.status === 401) { localStorage.removeItem(TOKEN); setToken(null); return; }
      if (r.ok) setS(await r.json());
    } catch { /* keep last numbers on network errors */ } finally { setLoading(false); }
  }, [token, days]);

  useEffect(() => {
    document.title = "Store stats | Swole Bears";
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div data-testid="stats-page" className="min-h-screen bg-[#FFF6EA] px-4 pb-16 text-[#1B1530] md:px-10">
      {!token ? <Login onToken={signIn} /> : (
        <div className="mx-auto max-w-6xl pt-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl">Store stats</h1>
              <p className="mt-1 text-sm text-[#4A4560]">swolebears.ca visitors. Updates every minute. Your own visits from this browser aren't counted.</p>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="stats-refresh-button" onClick={load} aria-label="Refresh" className="grid h-10 w-10 place-items-center rounded-full bg-white transition-colors hover:bg-[#1B1530] hover:text-white">
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </button>
              <button data-testid="stats-logout-button" onClick={signOut} className="flex h-10 items-center gap-2 rounded-full bg-white px-4 text-sm font-bold transition-colors hover:bg-[#1B1530] hover:text-white">
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {RANGES.map(([d, l]) => (
              <button key={d} data-testid={`stats-range-${d}`} onClick={() => setDays(d)}
                className={`h-10 rounded-full px-5 text-sm font-bold transition-colors ${days === d ? "bg-[#FF5A5F] text-white" : "bg-white hover:bg-[#1B1530]/10"}`}>{l}</button>
            ))}
          </div>
          <div className="mt-6">{s ? <Dashboard s={s} /> : <p data-testid="stats-loading" className="text-[#4A4560]">Loading numbers...</p>}</div>
          <Players token={token} />
        </div>
      )}
    </div>
  );
}
